import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaExternalLinkAlt, FaCalendarAlt, FaClock, FaUser, FaSpinner } from 'react-icons/fa';
import '../assets/components/_blog.scss';

const Blog = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mediumUsername = 'nahombirhan';

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await axios.get(
          `https://api.rss2json.com/v1/api.json?rss_url=https://medium.com/feed/@${mediumUsername}`,
          { timeout: 10000 }
        );
        
        if (response.data.status === 'ok') {
          const processedArticles = response.data.items.map((item) => ({
            ...item,
            excerpt: extractExcerpt(item.description),
            readTime: calculateReadTime(item.description),
            categories: extractCategories(item.categories)
          }));
          
          setArticles(processedArticles);
        } else {
          throw new Error('Failed to fetch articles');
        }
      } catch (error) {
        console.error('Error fetching Medium articles:', error);
        setError('Unable to load articles. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [mediumUsername]);

  const extractExcerpt = (description) => {
    if (!description) return '';
    const textOnly = description.replace(/<[^>]*>/g, '');
    return textOnly.length > 200 ? textOnly.substring(0, 200) + '...' : textOnly;
  };

  const calculateReadTime = (content) => {
    if (!content) return 1;
    const wordsPerMinute = 200;
    const wordCount = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    const readTime = Math.ceil(wordCount / wordsPerMinute);
    return readTime || 1;
  };

  const extractCategories = (categories) => {
    if (!categories || !Array.isArray(categories)) return [];
    return categories.slice(0, 3);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="content-wrapper">
          <div className="loading-state">
            <FaSpinner className="spinner" />
            <p>Loading articles...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="content-wrapper">
          <div className="error-state">
            <h2>Oops! Something went wrong</h2>
            <p>{error}</p>
            <button onClick={() => window.location.reload()} className="retry-button">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="content-wrapper">
        {/* Header Section */}
        <div className="section-header">
          <h1>Technical Blog</h1>
          <p>AI Security | Robustness, Human Alignment, Privacy</p>
          <div className="author-info">
            <FaUser className="author-icon" />
            <span>@{mediumUsername}</span>
          </div>
        </div>

        {/* Articles List */}
        <div className="blog-list">
          {articles.length === 0 ? (
            <div className="clean-card">
              <h3>No articles found</h3>
              <p>Articles will appear here once they're published.</p>
            </div>
          ) : (
            articles.map((article, index) => (
              <article key={article.guid || index} className="clean-card blog-post">
                <div className="post-header">
                  <h3 className="post-title">{article.title}</h3>
                  <div className="post-meta">
                    <div className="meta-item">
                      <FaCalendarAlt className="meta-icon" />
                      <span>{formatDate(article.pubDate)}</span>
                    </div>
                    <div className="meta-item">
                      <FaClock className="meta-icon" />
                      <span>{article.readTime} min read</span>
                    </div>
                  </div>
                </div>

                <div className="post-content">
                  <p className="post-excerpt">{article.excerpt}</p>
                  
                  {article.categories && article.categories.length > 0 && (
                    <div className="post-categories">
                      {article.categories.map((category, idx) => (
                        <span key={idx} className="category-tag">
                          {category}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="post-footer">
                  <a 
                    href={article.link} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="btn btn-primary"
                  >
                    Read Full Article
                    <FaExternalLinkAlt className="external-icon" />
                  </a>
                </div>
              </article>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Blog;